import React, { useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { submitSimUserDetails } from '../../../Services/APIs/scan';
import TermsAndConditions from './termsConditions';
import CountryCodeSelect from './CountryCodeSelect';
import { getLocalStorageJSON } from '../../../Utils/safeLocalStorage';
import { logEvent } from '../../../Utils/logEvent';

const validationSchema = Yup.object().shape({
    first_name: Yup.string().required('First name is required'),
    last_name: Yup.string().required('Last name is required'),
    zip: Yup.number().required('Zipcode is required'),
    email: Yup.string().email('Invalid email').required('Email is required'),
    country_code: Yup.string().required('Country code is required'),
    phone: Yup.string()
        .matches(/^(?=(?:.*\d){6,})[0-9 ()-]{6,15}$/, 'Enter a valid phone number')
        .required('Phone number is required'),
    terms: Yup.boolean().oneOf([true], 'You must accept the terms and conditions'),
});

const AboutMeForm = ({ toggleActive }) => {
    const [isTermsVisible, setIsTermsVisible] = useState(false);
    const [submitError, setSubmitError] = useState("");
    const uid = getLocalStorageJSON("usertpi").usertpi;
    const company = localStorage.getItem("company");

    const initialValues = {
        first_name: '',
        last_name: '',
        zip: '',
        email: '',
        country_code: '+1',
        phone: '',
        terms: false,
        is_consent_given: false,
    };

    const handleSubmit = async (values, { setSubmitting }) => {
        setSubmitting(true);
        setSubmitError("");
        localStorage.setItem("is_consent_given", JSON.stringify(values.is_consent_given));

        const payload = { ...values, is_form_completed: true, patient_UUID: uid, company }
        logEvent({ app: company, uid, event: "submitting form..." })
        await submitSimUserDetails(payload).then((res) => {
            if (res.status === 200) {
                localStorage.setItem("is_form", "true");
                setTimeout(() => {
                    toggleActive();
                }, 100);
            }
        }).catch((err) => {
            logEvent({ app: company, uid, event: "submitSimUserDetails failed", error: err });
            setSubmitError("We couldn't save your details. Please try again.");
        })
        setSubmitting(false);
    };

    return (
        <>
            <TermsAndConditions isVisible={isTermsVisible} onClose={() => setIsTermsVisible(false)} />
            <div className="white min-h-full flex items-center justify-center font-novatica p-4">
                <div className="w-full max-w-lg">
                    <div className="text-left mb-8">
                        <h1 className="text-[50px] font-medium text-[#2C2D24]">About Me</h1>
                    </div>
                    <Formik
                        initialValues={initialValues}
                        validationSchema={validationSchema}
                        onSubmit={handleSubmit}
                    >
                        {({ isSubmitting, values, setFieldValue }) => (
                            <Form>
                                <div className="grid grid-cols-2 gap-4 mb-4">
                                    <div>
                                        <label htmlFor="first_name" className="block text-xs">Full Name</label>
                                        <Field type="text" name="first_name" placeholder="John" className="mt-1 block w-full px-3 py-2 bg-white border-2 border-[#BED0C5CC] rounded-md shadow-sm focus:outline-none focus:ring-[#7998ECCC] focus:border-[#515a74CC] focus:shadow-sm sm:text-sm" />
                                        <ErrorMessage name="first_name" component="div" className="text-red-500 text-sm mt-1" />
                                    </div>
                                    <div>
                                        <label htmlFor="last_name" className="block text-xs">&nbsp;</label>
                                        <Field type="text" name="last_name" placeholder="Doe" className="mt-1 block w-full px-3 py-2 bg-white border-2 border-[#BED0C5CC] rounded-md shadow-sm focus:outline-none focus:ring-[#7998ECCC] focus:border-[#515a74CC] focus:shadow-sm sm:text-sm" />
                                        <ErrorMessage name="last_name" component="div" className="text-red-500 text-sm mt-1" />
                                    </div>
                                </div>

                                <div className="mb-4">
                                    <label htmlFor="zip" className="block text-xs">Zipcode</label>
                                    <Field type="number" name="zip" placeholder="Zipcode" className="mt-1 block w-full px-3 py-2 bg-white border-2 border-[#BED0C5CC] rounded-md shadow-sm focus:outline-none focus:ring-[#7998ECCC] focus:border-[#515a74CC] focus:shadow-sm sm:text-sm" />
                                    <ErrorMessage name="zip" component="div" className="text-red-500 text-sm mt-1" />
                                </div>

                                <div className="mb-4">
                                    <label htmlFor="email" className="block text-xs">Email</label>
                                    <Field type="email" name="email" placeholder="Email" className="mt-1 block w-full px-3 py-2 bg-white border-2 border-[#BED0C5CC] rounded-md shadow-sm focus:outline-none focus:ring-[#7998ECCC] focus:border-[#515a74CC] focus:shadow-sm sm:text-sm" />
                                    <ErrorMessage name="email" component="div" className="text-red-500 text-sm mt-1" />
                                </div>

                                <div className="mb-4">
                                    <label htmlFor="phone" className="block text-xs">Phone Number</label>
                                    <div className="mt-1 flex rounded-md shadow-sm">
                                        <CountryCodeSelect
                                            value={values.country_code}
                                            onChange={(dialCode) => setFieldValue('country_code', dialCode)}
                                        />
                                        <Field type="tel" name="phone" placeholder="Phone Number" className="block w-2/3 flex-1 px-3 py-2 bg-white border-2 border-l-0 border-[#BED0C5CC] rounded-r-md focus:outline-none focus:ring-[#7998ECCC] focus:border-[#515a74CC] focus:shadow-sm sm:text-sm" />
                                    </div>
                                    <ErrorMessage name="country_code" component="div" className="text-red-500 text-sm mt-1" />
                                    <ErrorMessage name="phone" component="div" className="text-red-500 text-sm mt-1" />
                                </div>

                                <div className="mb-6">
                                    <label className="flex items-center">
                                        <Field type="checkbox" name="terms" className="mt-1 h-4 w-4 shrink-0 text-[#7998EC1A] focus:ring-[#7998ECCC] focus:border-[#515a74CC] border-[#BED0C5CC] rounded" />
                                        <span className="ml-2 text-sm text-black">I accept the <button type="button" onClick={() => setIsTermsVisible(true)} className="text-blue-600 hover:underline">Terms and Conditions</button></span>
                                    </label>
                                    <ErrorMessage name="terms" component="div" className="text-red-500 text-sm mt-1" />
                                </div>
                                <div className="mb-6">
                                    <label className="flex items-center">
                                        <Field type="checkbox" name="is_consent_given" className="mt-1 h-4 w-4 shrink-0 text-[#7998EC] focus:ring-[#7998ECCC] border-[#BED0C5CC] rounded" />
                                        <span className="ml-2 text-sm text-black">I agree to allow my simulation to be used for social media and marketing purposes.</span>
                                    </label>
                                    <ErrorMessage name="is_consent_given" component="div" className="text-red-500 text-sm mt-1" />
                                </div>

                                {submitError && <div className="text-red-500 text-sm mb-3">{submitError}</div>}
                                <button type="submit" disabled={isSubmitting} className=" w-fit flex justify-center py-3 px-6 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#2C2D24]">
                                    {submitError ? "Retry" : "Submit to See Your New Smile"}
                                </button>
                            </Form>
                        )}
                    </Formik>
                </div>
            </div>
        </>
    );
};

export default AboutMeForm;
